import { Backdrop, Box, Button, Grid, TextField, Typography } from '@mui/material';
import AppLoader from '../../../components/AppLoader/AppLoader';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import '../Installments/InstallmentsForm.scss';
import {
  buildCustomerIdYupRule,
  isValidCustomerIdLength,
} from '../../../utils/customerIdValidation';
import { resolveDiscoCompanyCodeForBranding } from '../../../utils/adminCompany';
import { portalName } from '../../../utils/PortalName';
import { cfpBaseUrl } from '../../../network/urls';
import CustomerInformationCard from '../../../components/CustomerInformationCard/CustomerInformationCard';
import Error from '../../../components/error/error';
import StepProgressBar from '../../../components/StepProgressBarForm/StepProgressBar';
import FormSubmissionSuccessDialog from '../../../components/FormSubmissionSuccessDialog/FormSubmissionSuccessDialog';
import { useSelector } from 'react-redux';
import {
  formatBillMonth,
  getInstallmentWithdrawalDetails,
  numberFormatting,
  registerInstallmentWithdrawal,
  subDivAPI,
} from '../../../services/Functions';
import {
  resolveMrcCategoryFromTariff,
  resolveMrcTariffDisplay,
} from '../MeterReadingCorrection/mrcReadingUtils';

const getTariffDescription = (tariffCode) => {
  const tariffMap = {
    '01': 'Domestic',
    '02': 'Commercial',
    '03': 'Commercial',
    '04': 'Industrial',
    '05': 'Bulk Supply',
    '06': 'Agricultural Tube Wells',
    '07': 'Street Lighting',
    '08': 'Residential Colonies',
  };
  return tariffMap[String(tariffCode || '').padStart(2, '0')] || 'General';
};

const formatIbsPhoneNo = (contactNo) => {
  if (!contactNo) return '';
  return String(contactNo).replace(/\D/g, '').replace(/^92/, '0');
};

const formatApiMonth = (value) => {
  if (!value) return '';
  const formatted = formatBillMonth(value);
  if (!formatted || formatted.includes('undefined') || formatted.includes('NaN')) return '';
  return formatted;
};

const formatApiDate = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const formatCnic = (value) => {
  if (value === null || value === undefined || value === '' || Number(value) === 0) return '';
  return String(value);
};

const toOracleDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}-${months[date.getMonth()]}-${yy}`;
};

const toApiNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return value;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const toApiInt = (value) => Math.trunc(toApiNumber(value));

const DisabledField = ({ label, name, value }) => (
  <Grid item xs={6} sm={4} md={2} lg={2} className="inst-bill-col">
    <div className="field-wrap">
      <Typography className="fieldLabel">{label}</Typography>
      <TextField name={name} fullWidth value={value || ''} disabled className="textInputField1" />
    </div>
  </Grid>
);

const WithdrawalOfInstallmentForm = () => {
  const initialValues = {
    iv_search_by: 'referenceNo',
    iv_consumer_id: '',
    iv_reference_number: '',
    iv_circle: '',
    iv_division: '',
    iv_sub_divison: '',
    iv_address: '',
    iv_applicant_name: '',
    iv_father_name: '',
    iv_applicant_cnic: '',
    iv_applicant_phone_no: '',
    iv_telco: '',
    iv_applicant_email: '',
    iv_connection_type: '',
    iv_tariff_description: '',
    iv_tariff_code: '',
    iv_meter_type: '',
    iv_phase: '',
    iv_cc_code: '',
    iv_adjustment_no: '',
    iv_withdrawal_amount: '',
    iv_net_for_bill: '',
    iv_bill_period: '',
    iv_adjustment_date: '',
    iv_start_month: '',
    iv_end_month: '',
    iv_installment_bill_month: '',
    iv_no_of_installment: '',
    iv_net_payable_while_installment: '',
    iv_remarks: '',
  };

  const [searchBy, setSearchBy] = useState('referenceNo');
  const [subDivision, setSubDivision] = useState({ code: null, name: '' });
  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [hierarchyList, setHierarchyList] = useState([]);
  const [circleList, setCircleList] = useState([]);
  const [divisionList, setDivisionList] = useState([]);
  const [subDivisionList, setSubDivisionList] = useState([]);
  const [defaultCircle, setDefaultCircle] = useState();
  const [defaultDivision, setDefaultDivision] = useState();
  const [referenceNumber, setReferenceNumber] = useState('');
  const [error, setError] = useState('');
  const [installmentRow, setInstallmentRow] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [statusSteps, setStatusSteps] = useState([
    { title1: 'Applicant', title2: 'Detail', status: 0 },
    { title1: 'Withdrawal', title2: 'Reason', status: 0 },
  ]);
  const [tarifData, setTarifData] = useState([]);

  const navigate = useNavigate();
  const { token, code: userCode, roleId, selectedAdminCompanyCode, csc } = useSelector((state) => state.auth);
  const subDivList = JSON.parse(window.localStorage.getItem('subDivisions'));
  const userCompany = resolveDiscoCompanyCodeForBranding(
    userCode,
    roleId,
    selectedAdminCompanyCode,
  );
  const companyCode = String(userCompany || userCode || '').slice(0, 2);

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, resetForm, submitCount, setValues } = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      iv_reference_number: Yup.string().when('iv_search_by', {
        is: () => searchBy === 'referenceNo',
        then: Yup.string().min(14, 'Reference No. must be 14 digits.').max(14, 'Reference No. must be 14 digits.').required('Enter reference number.'),
        otherwise: Yup.string().optional(),
      }),
      iv_consumer_id: Yup.string().when('iv_search_by', {
        is: () => searchBy === 'consumerId',
        then: buildCustomerIdYupRule(userCompany),
        otherwise: Yup.string().optional(),
      }),
      iv_circle: Yup.mixed().required('Please select a circle.'),
      iv_division: Yup.mixed().required('Please select a division.'),
      iv_sub_divison: Yup.mixed().required('Please select a sub division.'),
      iv_applicant_phone_no: Yup.string()
        .required('Please enter phone number.')
        .matches(/^03[0-9]{9}$/, 'Enter Phone number as 03001234567'),
      iv_telco: Yup.string()
        .required('Please select a telco.')
        .test(
          'telco-selected',
          'Please select a telco.',
          (value) => ['1', '2', '3', '4'].includes(String(value ?? '').trim()),
        ),
      iv_remarks: Yup.string()
        .trim()
        .required('Please enter reason for withdrawal.')
        .min(10, 'Reason should be between 10 to 250 characters.')
        .max(250, 'Reason should be between 10 to 250 characters.'),
    }),
    onSubmit: async (formValues, formikHelpers) => {
      if (!installmentRow) {
        setError('Please fetch installment details before submitting.');
        return;
      }

      setViewBackdrop(true);
      setError('');
      try {
        const tariffCode = String(formValues.iv_tariff_code || installmentRow.TARIFF_ACTIVE || '').padStart(2, '0');
        const connectionType = String(
          formValues.iv_connection_type || getTariffDescription(tariffCode),
        ).toUpperCase();
        const payload = {
          submittedThrough: portalName,
          cfcId: String(csc || formValues.iv_cc_code || ''),
          referenceNo: String(formValues.iv_reference_number || referenceNumber || '').replace(/\s/g, ''),
          customerId: String(formValues.iv_consumer_id || ''),
          consumerName: String(formValues.iv_applicant_name || '').trim(),
          consumerCNIC: String(formValues.iv_applicant_cnic || ''),
          consumerEmail: String(formValues.iv_applicant_email || ''),
          consumerPhone: String(formValues.iv_applicant_phone_no || ''),
          consumerAddress: String(formValues.iv_address || '').trim(),
          telcoCode: String(formValues.iv_telco || '').trim(),
          connectionType,
          tariffDesc: formValues.iv_tariff_description || installmentRow.TARIFF_DESC || connectionType,
          tariffCode,
          ccCode: String(formValues.iv_cc_code || ''),
          IPSAdjustmentNo: String(installmentRow.ADJUSTMENT_NO ?? formValues.iv_adjustment_no ?? '').replace(/\.0+$/, ''),
          IPSAdjustmentDate: toOracleDate(installmentRow.ADJUSTMENT_DATE),
          startMonth: toOracleDate(installmentRow.START_MONTH),
          endMonth: toOracleDate(installmentRow.END_MONTH),
          installmentBillMonth: toOracleDate(installmentRow.INSTALLMENT_BILL_MONTH),
          noOfInstallments: toApiInt(installmentRow.NO_OF_INSTALLMENT ?? formValues.iv_no_of_installment),
          netForBill: toApiNumber(installmentRow.NET_FOR_BILL ?? formValues.iv_net_for_bill),
          netPayableWhileInstallment: toApiNumber(
            installmentRow.NET_PAYABLE_WHILE_INSTALLMENT ?? formValues.iv_net_payable_while_installment,
          ),
          withdrawalAmount: toApiNumber(installmentRow.WITHDRAWALAMOUNT ?? formValues.iv_withdrawal_amount),
          withdrawalReason: String(formValues.iv_remarks || '').trim(),
        };

        const result = await registerInstallmentWithdrawal(token, payload);
        if (String(result?.status) === '200') {
          const trackingId =
            result.applicationId ||
            result.eOpsTrackingId ||
            result.trackingId ||
            result.withdrawalRequestId ||
            result.billInstallmentRequestId ||
            '';
          setApplicationId(trackingId);
          setSuccessOpen(true);
          setInstallmentRow(null);
          setReferenceNumber('');
          setDefaultCircle('');
          setDefaultDivision('');
          setSubDivision('');
          formikHelpers.resetForm({ values: { ...initialValues, iv_search_by: searchBy } });
        } else {
          setError(result?.message || result?.innerMessage || 'Unable to register withdrawal of installment.');
        }
      } catch (e) {
        setError('An error occurred while registering withdrawal of installment.');
      } finally {
        setViewBackdrop(false);
      }
    },
  });

  const SearchBy = (event) => {
    const nextSearchBy = event.target.value;
    setSearchBy(nextSearchBy);
    setDefaultCircle('');
    setDefaultDivision('');
    setSubDivision('');
    setReferenceNumber('');
    setInstallmentRow(null);
    setError('');
    resetForm({
      values: {
        ...initialValues,
        iv_search_by: nextSearchBy,
      },
    });
  };

  const tariffListAPI = async () => {
    if (!token) return [];
    try {
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/getTariffsDetail', {
        method: 'POST',
        body: new FormData(),
        headers: myHeaders,
        redirect: 'follow',
      });
      const result = await response.json();
      if (String(result?.status) === '200' && Array.isArray(result?.data)) {
        setTarifData(result.data);
        return result.data;
      }
    } catch (e) {
      /* ignored */
    }
    return [];
  };

  const resolveTariffFields = async (tariffCode, rowDesc = '') => {
    let tariffs = tarifData;
    if (!tariffs?.length) {
      tariffs = (await tariffListAPI()) || [];
    }
    const category =
      resolveMrcCategoryFromTariff(tariffCode, tariffs) || getTariffDescription(tariffCode);
    const fromList = resolveMrcTariffDisplay(tariffCode, tariffs);
    const hasTariffName = Boolean(fromList) && !/^\d{1,2}$/.test(String(fromList).trim());
    const tariffDescription =
      (hasTariffName ? fromList : '') ||
      String(rowDesc || '').trim() ||
      fromList ||
      tariffCode;
    return { category, tariffDescription };
  };

  const applyInstallmentToForm = async (row) => {
    const refNo = String(row.CKEY || '').replace(/\s/g, '').slice(0, 14);
    const customerId = String(row.APPLICATION_NO || '');
    const tariffCode = String(row.TARIFF_ACTIVE || '');
    const { category, tariffDescription } = await resolveTariffFields(tariffCode, row.TARIFF_DESC);

    setValues({
      ...values,
      iv_reference_number: searchBy === 'referenceNo' ? values.iv_reference_number : refNo,
      iv_consumer_id: searchBy === 'consumerId' ? values.iv_consumer_id : customerId,
      iv_circle: refNo.length >= 5 ? parseInt(refNo.slice(2, 5), 10) : values.iv_circle,
      iv_division: refNo.length >= 6 ? parseInt(refNo.slice(2, 6), 10) : values.iv_division,
      iv_sub_divison: refNo.length >= 7 ? parseInt(refNo.slice(2, 7), 10) : values.iv_sub_divison,
      iv_address: String(row.ADDRESS || '').trim(),
      iv_applicant_name: String(row.CONSUMER_NAME || '').trim(),
      iv_applicant_cnic: formatCnic(row.NEW_NIC_NO),
      iv_applicant_phone_no: formatIbsPhoneNo(row.CONTACT_NO),
      iv_telco: '',
      iv_applicant_email: row.EMAIL_ADDR || '',
      iv_connection_type: category,
      iv_tariff_description: tariffDescription,
      iv_tariff_code: tariffCode,
      iv_cc_code: row.CC_CODE || '',
      iv_adjustment_no: row.ADJUSTMENT_NO ?? '',
      iv_withdrawal_amount: numberFormatting(row.WITHDRAWALAMOUNT ?? 0),
      iv_net_for_bill: numberFormatting(row.NET_FOR_BILL ?? 0),
      iv_bill_period: formatApiMonth(row.B_PERIOD),
      iv_adjustment_date: formatApiDate(row.ADJUSTMENT_DATE),
      iv_start_month: formatApiMonth(row.START_MONTH),
      iv_end_month: formatApiMonth(row.END_MONTH),
      iv_installment_bill_month: formatApiMonth(row.INSTALLMENT_BILL_MONTH),
      iv_no_of_installment: row.NO_OF_INSTALLMENT ?? '',
      iv_net_payable_while_installment: numberFormatting(row.NET_PAYABLE_WHILE_INSTALLMENT ?? 0),
    });
    setInstallmentRow(row);
    setReferenceNumber(refNo);
  };

  const referenceApi = async () => {
    const searchValue = String(
      searchBy === 'consumerId' ? values.iv_consumer_id : values.iv_reference_number || '',
    ).replace(/\s/g, '');
    if (!searchValue || !companyCode) return;

    setViewBackdrop(true);
    try {
      const apiResponse = await getInstallmentWithdrawalDetails(token, searchValue, companyCode);
      const hasData =
        (apiResponse?.status === '200' || apiResponse?.status === 200) &&
        Array.isArray(apiResponse?.data) &&
        apiResponse.data.length > 0;

      if (hasData) {
        await applyInstallmentToForm(apiResponse.data[0]);
        setError('');
      } else {
        setInstallmentRow(null);
        setError(
          apiResponse?.message ||
          apiResponse?.innerMessage ||
          (searchBy === 'consumerId'
            ? 'No installment found against this Customer ID.'
            : 'No installment found against this reference No.'),
        );
      }
    } catch (e) {
      setInstallmentRow(null);
      setError('An error occurred while fetching installment details.');
    } finally {
      setViewBackdrop(false);
    }
  };

  useEffect(() => {
    const loadSubDivs = async () => {
      if (userCompany && token) {
        setViewBackdrop(true);
        const lists = await subDivAPI(subDivList);
        setHierarchyList(lists.hierarchyList);
        setCircleList(lists.circleList);
        setDivisionList(lists.divisionList);
        setSubDivisionList(lists.subDivisionList);
        await tariffListAPI();
        setViewBackdrop(false);
      }
    };
    loadSubDivs();
  }, []);

  useEffect(() => {
    if (!hierarchyList || !defaultCircle) return;
    const uniqueDivisions = hierarchyList
      .filter((division) => division.circleCode === defaultCircle.code)
      .reduce((unique, division) => {
        if (!unique.find((item) => item.code === division.divisionCode)) {
          unique.push({ code: division.divisionCode, name: division.divisionName });
        }
        return unique;
      }, []);
    setDivisionList(uniqueDivisions.sort((a, b) => a.name.localeCompare(b.name, 'en', { ignorePunctuation: true })));
  }, [defaultCircle, hierarchyList]);

  useEffect(() => {
    if (!defaultDivision || !hierarchyList) return;
    const uniqueSubDivisions = hierarchyList
      .filter((item) => item.divisionCode === defaultDivision.code)
      .reduce((unique, item) => {
        if (!unique.find((row) => row.code === item.subDivisionCode)) {
          unique.push({ code: item.subDivisionCode, name: item.subDivisionName });
        }
        return unique;
      }, []);
    setSubDivisionList(uniqueSubDivisions.sort((a, b) => a.name.localeCompare(b.name, 'en', { ignorePunctuation: true })));
  }, [defaultDivision, hierarchyList]);

  useEffect(() => {
    if (referenceNumber && referenceNumber.length === 14 && circleList.length && hierarchyList.length) {
      const circleCode = referenceNumber.slice(2, 5);
      const circleObject = circleList.find((item) => item.code === parseInt(circleCode, 10));
      if (!circleObject) {
        setError(`Circle not found with code: ${circleCode}`);
        return;
      }
      setDefaultCircle(circleObject);
      values.iv_circle = circleObject.code;

      const divisionCode = referenceNumber.slice(2, 6);
      const subDivisionCode = referenceNumber.slice(2, 7);
      const uniqueDivisions = [];
      hierarchyList.forEach((division) => {
        if (division.circleCode === parseInt(circleCode, 10) && !uniqueDivisions.find((row) => row.code === division.divisionCode)) {
          uniqueDivisions.push({ code: division.divisionCode, name: division.divisionName });
        }
      });
      const defDivision = uniqueDivisions.find((item) => item.code === parseInt(divisionCode, 10));
      if (!defDivision) {
        setError(`Division not found with code: ${divisionCode}. Contact Admin!`);
        return;
      }
      setDefaultDivision(defDivision);
      values.iv_division = defDivision.code;
      setDivisionList(uniqueDivisions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));

      const uniqueSubDivisions = [];
      hierarchyList.forEach((item) => {
        if (item.divisionCode === parseInt(divisionCode, 10) && !uniqueSubDivisions.find((row) => row.code === item.subDivisionCode)) {
          uniqueSubDivisions.push({ code: item.subDivisionCode, name: item.subDivisionName });
        }
      });
      const subDivisionObject = uniqueSubDivisions.find((item) => item.code === parseInt(subDivisionCode, 10));
      if (!subDivisionObject) {
        setError(`Subdivision not found with code: ${subDivisionCode}. Contact Admin!`);
        return;
      }
      values.iv_sub_divison = subDivisionObject.code;
      setSubDivision(subDivisionObject);
      setSubDivisionList(uniqueSubDivisions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));
    }
  }, [referenceNumber, circleList, hierarchyList]);

  useEffect(() => {
    if (searchBy === 'referenceNo' && values.iv_reference_number.length === 14) {
      referenceApi();
    }
  }, [values.iv_reference_number]);

  useEffect(() => {
    if (searchBy === 'consumerId' && isValidCustomerIdLength(values.iv_consumer_id, userCompany)) {
      referenceApi();
    }
  }, [values.iv_consumer_id]);

  useEffect(() => {
    const applicantComplete = Boolean(
      (isValidCustomerIdLength(values.iv_consumer_id, userCompany) || values.iv_reference_number?.length === 14) &&
      values.iv_applicant_name &&
      /^03[0-9]{9}$/.test(String(values.iv_applicant_phone_no || '')) &&
      ['1', '2', '3', '4'].includes(String(values.iv_telco || '').trim()),
    );
    setStatusSteps([
      { title1: 'Applicant', title2: 'Detail', status: applicantComplete ? 1 : 0 },
      { title1: 'Withdrawal', title2: 'Reason', status: values.iv_remarks?.trim() ? 1 : 0 },
    ]);
  }, [
    values.iv_consumer_id,
    values.iv_reference_number,
    values.iv_applicant_name,
    values.iv_applicant_phone_no,
    values.iv_telco,
    values.iv_remarks,
    userCompany,
  ]);

  const canSubmit = Boolean(
    values.iv_applicant_name &&
    (isValidCustomerIdLength(values.iv_consumer_id, userCompany) || values.iv_reference_number.length === 14) &&
    values.iv_no_of_installment &&
    /^03[0-9]{9}$/.test(String(values.iv_applicant_phone_no || '')) &&
    ['1', '2', '3', '4'].includes(String(values.iv_telco || '').trim()) &&
    values.iv_remarks?.trim() &&
    installmentRow &&
    !error &&
    !viewBackdrop,
  );

  const handleCardChange = (event) => {
    const name = event?.target?.name;
    const value = event?.target?.value;
    if (name === 'iv_applicant_phone_no') {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
      handleChange({ target: { name, value: digits } });
      return;
    }
    if (name === 'iv_telco') {
      handleChange({ target: { name, value: String(value ?? '') } });
      return;
    }
    handleChange(event);
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    navigate('/withdrawal-of-installment-table');
  };

  return (
    <>
      <Grid container className="inst-page" sx={{ width: '100%', maxWidth: '100%' }}>
        <Box className="inst-container" sx={{ width: '100%', maxWidth: '100%' }}>
          <h3 className="heading inst-page-title">Withdrawal of Installment</h3>
          <div className="inst-stepper-wrap inst-stepper-wrap--multi">
            <StepProgressBar array={statusSteps} pageName="WOI Form" />
          </div>
          <form onSubmit={handleSubmit} noValidate className="inst-form">
            <div className="section-card section-card--accent">
              <div className="section-title-row">
                <div className="section-title">Applicant&apos;s Detail</div>
              </div>
              <CustomerInformationCard
                searchBy={searchBy}
                SearchBy={SearchBy}
                errors={errors}
                values={values}
                touched={touched}
                handleChange={handleCardChange}
                handleBlur={handleBlur}
                defaultCircle={defaultCircle}
                circleList={circleList}
                setDefaultCircle={setDefaultCircle}
                defaultDivision={defaultDivision}
                divisionList={divisionList}
                setDefaultDivision={setDefaultDivision}
                subDivision={subDivision}
                subDivisionList={subDivisionList}
                setSubDivision={setSubDivision}
                showSubmitErrors={submitCount > 0}
                layoutVariant="inst"
                showTelco
                hideMeterType
                hidePhase
              />
            </div>

            <div className="section-card section-card--bill">
              <div className="section-title">Installment / Withdrawal Detail</div>
              <Grid container spacing={0} className="container inst-billing-grid">
                <DisabledField label="No. of Installments" name="iv_no_of_installment" value={values.iv_no_of_installment} />
                <DisabledField label="Installment Bill Month" name="iv_installment_bill_month" value={values.iv_installment_bill_month} />
                <DisabledField label="Start Month" name="iv_start_month" value={values.iv_start_month} />
                <DisabledField label="End Month" name="iv_end_month" value={values.iv_end_month} />
                <DisabledField label="Withdrawal Amount" name="iv_withdrawal_amount" value={values.iv_withdrawal_amount} />
                <DisabledField label="Net for Bill" name="iv_net_for_bill" value={values.iv_net_for_bill} />
                <DisabledField label="Net Payable While Installment" name="iv_net_payable_while_installment" value={values.iv_net_payable_while_installment} />
                <DisabledField label="Bill Period" name="iv_bill_period" value={values.iv_bill_period} />
                {/* <DisabledField label="Adjustment No." name="iv_adjustment_no" value={values.iv_adjustment_no} />
                <DisabledField label="Adjustment Date" name="iv_adjustment_date" value={values.iv_adjustment_date} /> */}
              </Grid>
              <div className="woi-reason-field">
                <Typography className="fieldLabel">Reason for Withdrawal</Typography>
                <TextField
                  name="iv_remarks"
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  maxRows={8}
                  placeholder="Enter reason for withdrawal (min. 10, max. 250 characters)"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.iv_remarks}
                  inputProps={{ maxLength: 250 }}
                  className="textInputField1 woi-reason-input"
                />
                <Box className="from-Error">
                  {errors.iv_remarks && touched.iv_remarks ? errors.iv_remarks : null}
                </Box>
              </div>
            </div>

            {error ? (
              <div className="inst-alert-wrap">
                <Error error={error} onClose={() => setError('')} />
              </div>
            ) : null}

            <div className="submit-area">
              <Button type="submit" variant="contained" className="submit-btn" disabled={!canSubmit}>
                Submit
              </Button>
            </div>
          </form>
        </Box>
      </Grid>

      <FormSubmissionSuccessDialog
        open={successOpen}
        onClose={handleSuccessClose}
        title="Application Submitted"
        message="Your withdrawal of installment request is submitted successfully."
        trackingId={applicationId}
      />

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={viewBackdrop}>
        <AppLoader />
      </Backdrop>
    </>
  );
};

export default WithdrawalOfInstallmentForm;
